// The durable **practice credential** store (`rounds-practice-sandbox`, plan **D17**).
//
// A **second, independent store**, never a mode of the tournament one. That separation is the whole
// requirement (`client-app` "The two credentials are independent"), and it is structural here rather
// than a convention: a different `localStorage` key, a different record shape (so neither store can
// read the other's value even under the wrong key), and no code path that presents one where the
// other is expected. Sharing a store would mean a tournament credential the server rejects takes a
// player's practice colony down with it, and would let a **device-minted** practice credential be
// offered to the tournament mint, which is a self-mintable key on a metered path.
//
// It follows `rounds-credential-store.ts` in every other respect, because that file is the
// established shape for this class of persistence: a `StorageLike` shim accepted as a parameter,
// try/catch around **every** storage call, strict runtime validation of the untrusted stored value,
// degrade-to-absent rather than throw, and a `version` field so a shape change is a migration
// rather than a silent misread.
//
// Two differences follow from practice being device-local:
//
// One difference follows from practice being device-local: **the credential is minted here, not by
// the server.** There is no tournament membership to key on and no join code to present, so the
// device generates its own 256-bit secret.
//
// This record used to carry a second field — the operator practice code, which gated the Beekeeper
// because a self-mintable key cannot itself be a meter (architecture review **F2**). That code is
// gone: on a container deployment nobody reads the console it was printed to, so it gated the player
// rather than the spend, and where a deployment configures Entra authentication the tenant is the
// boundary. The Beekeeper is metered by the server's own spend and concurrency ceilings instead.
//
// **The stored `version` deliberately does not change.** A record written before this carries a
// `code` key the validator no longer looks at, so it still validates and the player keeps their
// practice colony; the stale value is dropped the next time the record is written. Bumping the
// version would have been the tidy change and would have deleted every existing sandbox to do it.

import type { StorageLike } from './rounds-credential-store';

export type { StorageLike };

/** The `localStorage` key this adapter owns. Distinct from `waggle-rounds-credential` (the
 * tournament membership), `waggle-session` (a tab-scoped live match) and `waggle-audio-prefs`. */
export const PRACTICE_CREDENTIAL_KEY = 'waggle-rounds-practice-credential';

/** The current stored-shape version. Any other value degrades to "nothing stored". */
export const PRACTICE_CREDENTIAL_VERSION = 1;

/** 32 bytes, hex — the same width the server's own credentials use, so the two are
 * indistinguishable in strength while remaining separate in provenance. */
const CREDENTIAL_PATTERN = /^[0-9a-f]{64}$/i;

const CREDENTIAL_BYTES = 32;

export interface PracticeCredentialRecord {
  version: number;
  /** The device-minted 256-bit practice secret, hex. The only key to this device's practice
   * sandbox; there is no cross-device resume by design (**D17**). */
  credential: string;
}

/**
 * Runtime-validates an untrusted parsed value. Returns `null` on ANY failure, which is what lets a
 * malformed value degrade to "no credential" instead of being presented to the server.
 *
 * A **tournament** record fails this check because it carries `displayName`/`sandbox`, and that
 * rejection is now **explicit**. It used to be incidental: a tournament record simply had no `code`
 * key, and this validator required one. Removing `code` removed the discriminator with it and let a
 * tournament record validate as a practice one — so the field the two shapes genuinely differ by is
 * named here instead of relied on as a side effect.
 */
export function validatePracticeCredential(value: unknown): PracticeCredentialRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.version !== PRACTICE_CREDENTIAL_VERSION) {
    return null;
  }
  if (typeof record.credential !== 'string' || !CREDENTIAL_PATTERN.test(record.credential)) {
    return null;
  }
  // The **tournament** record's own fields, refused by name (see this function's doc). A record
  // carrying either is that store's, whatever key it was written under.
  if (record.displayName !== undefined || record.sandbox !== undefined) {
    return null;
  }
  return {
    version: PRACTICE_CREDENTIAL_VERSION,
    credential: record.credential,
  };
}

/**
 * The randomness seam. A **seam, not a direct `crypto` read**, for one reason: the credential is the
 * only key to a player's practice colony, so a test that could silently fall back to a weak source
 * would be a test that cannot notice the weakness. The production default is
 * `crypto.getRandomValues`; there is deliberately no `Math.random` fallback — an environment with no
 * CSPRNG fails the capability gate before this is ever reached.
 */
export interface PracticeRandom {
  bytes(count: number): Uint8Array;
}

export function cryptoPracticeRandom(source: Crypto | undefined = globalThis.crypto): PracticeRandom {
  return {
    bytes(count: number): Uint8Array {
      const out = new Uint8Array(count);
      if (source === undefined || typeof source.getRandomValues !== 'function') {
        // Unreachable behind the capability gate. Throwing rather than degrading is the point:
        // a credential from a predictable source is worse than no practice session.
        throw new Error('no cryptographic randomness available');
      }
      source.getRandomValues(out);
      return out;
    },
  };
}

/** A deterministic randomness double for tests — `seed`, then `seed+1`, … so a minted credential is
 * reproducible without being confusable with a real one. */
export function fixedPracticeRandom(seed: number): PracticeRandom {
  return {
    bytes(count: number): Uint8Array {
      const out = new Uint8Array(count);
      for (let index = 0; index < count; index += 1) {
        out[index] = (seed + index) & 0xff;
      }
      return out;
    },
  };
}

function hex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, '0');
  }
  return out;
}

/** The practice credential store seam. Never throws to the caller. */
export interface PracticeCredentialStore {
  /** The stored record, or `null` when nothing valid is stored. */
  get(): PracticeCredentialRecord | null;
  /**
   * The stored record, minting and persisting one if there is none.
   *
   * Called from the practice entry point rather than at boot, so a player who never taps
   * "Play in sandbox" never has a practice credential written — the same restraint
   * `client-app`'s "the bootstrap issues no mint before the player acts" imposes on the
   * tournament path, applied to the device's own secret.
   */
  ensure(): PracticeCredentialRecord;
  /** Forgets the whole record — "start over" on a device the player is done with. */
  clear(): void;
}

export function createPracticeCredentialStore(
  storage: StorageLike,
  random: PracticeRandom = cryptoPracticeRandom(),
  key: string = PRACTICE_CREDENTIAL_KEY,
): PracticeCredentialStore {
  function read(): PracticeCredentialRecord | null {
    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      return null; // storage unavailable (private mode, a sandboxed SecurityError)
    }
    if (raw === null) {
      return null;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null; // malformed JSON — nothing stored, never repaired in place
    }
    return validatePracticeCredential(parsed);
  }

  function write(record: PracticeCredentialRecord): void {
    try {
      storage.setItem(key, JSON.stringify(record));
    } catch {
      // Quota, private mode, sandboxed. The practice colony simply is not recoverable after this
      // tab closes; failing the flow over it would be worse, and the single-device framing already
      // tells the player their session is device-held.
    }
  }

  /** Held in memory so a storage-less browser still gets one stable credential per load rather
   * than a fresh one on every `ensure()`, which would open a new sandbox per Run press. */
  let cached: PracticeCredentialRecord | null = null;

  return {
    get(): PracticeCredentialRecord | null {
      return read() ?? cached;
    },
    ensure(): PracticeCredentialRecord {
      const existing = read() ?? cached;
      if (existing !== null) {
        return existing;
      }
      const minted: PracticeCredentialRecord = {
        version: PRACTICE_CREDENTIAL_VERSION,
        credential: hex(random.bytes(CREDENTIAL_BYTES)),
      };
      cached = minted;
      write(minted);
      return minted;
    },
    clear(): void {
      cached = null;
      try {
        storage.removeItem(key);
      } catch {
        // A value we cannot remove is still one this load will not present again, because the
        // in-memory copy is dropped above.
      }
    },
  };
}
